import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaShoppingCart } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { productDetailPath } from "../../constants/routes";
import { useLocalizedProduct } from "../../hooks/useLocalizedProduct";
import { useCartStore } from "../../store/cartStore";
import { getProductUnitDisplay } from "../../utils/mapWooProduct";
import "./ProductCard.css";

function getDisplayCategory(categories = []) {
  return categories.find((category) => category.slug !== "uncategorized");
}

function getStockStatusLabel(stockStatus, t) {
  switch (stockStatus) {
    case "instock":
      return t("In stock");
    case "outofstock":
      return t("Out of stock");
    case "onbackorder":
      return t("On backorder");
    default:
      return stockStatus;
  }
}

function ProductCard({ product, showAddToCart = false }) {
  const { t } = useTranslation();
  const localizedProduct = useLocalizedProduct(product);
  const addItem = useCartStore((state) => state.addItem);
  const [added, setAdded] = useState(false);

  if (!localizedProduct) return null;

  const detailPath = productDetailPath(localizedProduct.slug);
  const category = getDisplayCategory(localizedProduct.categories);
  const badgeLabel = category?.name || t("Product");
  const hasPrice = Boolean(localizedProduct.formattedPrice);
  const unitDisplay = getProductUnitDisplay(localizedProduct);
  const hasUnitInfo = Boolean(unitDisplay);

  const handleAddToCart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    addItem(localizedProduct, 1);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2000);
  };

  return (
    <article className="caf-product-card">
      <Link to={detailPath} className="caf-product-card__link">
        <div className="caf-product-card__media">
          {localizedProduct.image ? (
            <img
              src={localizedProduct.image}
              alt={localizedProduct.name}
              className="caf-product-card__image"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="caf-product-card__placeholder" aria-hidden="true" />
          )}
        </div>

        <div className="caf-product-card__body">
          <div className="caf-product-card__meta-row">
            <span className="caf-product-card__category">{badgeLabel}</span>
            <span
              className={`caf-product-card__stock caf-product-card__stock--${localizedProduct.stockStatus}`}
            >
              {getStockStatusLabel(localizedProduct.stockStatus, t)}
            </span>
          </div>
          <h3 className="caf-product-card__title">{localizedProduct.name}</h3>

          {hasPrice || hasUnitInfo ? (
            <div className="caf-product-card__price-row" dir="ltr">
              {hasPrice ? (
                <span className="caf-product-card__price">{localizedProduct.formattedPrice}</span>
              ) : (
                <span className="caf-product-card__price caf-product-card__price--empty" />
              )}
              {hasUnitInfo ? (
                <span className="caf-product-card__pack">{unitDisplay}</span>
              ) : null}
            </div>
          ) : (
            <p className="caf-product-card__quote">{t("Request a Quote")}</p>
          )}
        </div>
      </Link>

      <div className="caf-product-card__footer">
        {showAddToCart ? (
          <button
            type="button"
            className="caf-product-card__btn"
            onClick={handleAddToCart}
            disabled={!localizedProduct.purchasable || !localizedProduct.inStock}
          >
            <FaShoppingCart className="caf-product-card__btn-icon" aria-hidden="true" />
            <span>{added ? t("Added to cart") : t("Add to cart")}</span>
          </button>
        ) : (
          <Link to={detailPath} className="caf-product-card__btn caf-product-card__btn--outline">
            {t("View product")}
          </Link>
        )}
      </div>
    </article>
  );
}

export default ProductCard;
